import { Pressable, View, Text, StyleSheet } from "react-native";

import { colors, fonts, spacing, radius } from "@/lib/theme";

export type SectionRowStatus = "not_started" | "in_progress" | "completed" | "locked" | "next";

interface SectionRowProps {
    name: string;
    questionCount: number;
    behaviorType: string | null;
    status: SectionRowStatus;
    completedCount?: number;
    onPress: () => void;
    isLast?: boolean;
}

const BEHAVIOR_BADGES: Record<string, { label: string; bg: string; text: string }> = {
    clinical_5step: { label: "5-STEP", bg: colors.pink50, text: colors.pink400 },
    clinical_4step: { label: "4-STEP", bg: colors.pink50, text: colors.pink400 },
    data_entry_validated: { label: "ENTRY", bg: colors.teal50, text: colors.teal500 },
    data_entry: { label: "ENTRY", bg: colors.teal50, text: colors.teal500 },
    medical_history: { label: "ENTRY", bg: colors.teal50, text: colors.teal500 },
    teaching: { label: "TEACHING", bg: colors.teal50, text: colors.teal600 }
};

function StatusDot({ status }: { status: SectionRowStatus }) {
    if (status === "completed") {
        return (
            <View style={[styles.dot, styles.dotCompleted]}>
                <Text style={styles.checkmark}>✓</Text>
            </View>
        );
    }
    if (status === "locked") {
        return (
            <View style={[styles.dot, styles.dotLocked]}>
                <Text style={styles.lockIcon}>L</Text>
            </View>
        );
    }
    if (status === "in_progress" || status === "next") {
        return <View style={[styles.dot, styles.dotActive]} />;
    }
    return <View style={[styles.dot, styles.dotEmpty]} />;
}

function ChevronOrLock({ status }: { status: SectionRowStatus }) {
    if (status === "locked") {
        return (
            <View style={styles.lockBadge}>
                <Text style={styles.lockBadgeText}>L</Text>
            </View>
        );
    }
    if (status === "completed") {
        return (
            <View style={styles.checkBadge}>
                <Text style={styles.checkBadgeText}>✓</Text>
            </View>
        );
    }
    return <Text style={styles.chevron}>›</Text>;
}

export function SectionRow({
    name,
    questionCount,
    behaviorType,
    status,
    completedCount,
    onPress,
    isLast
}: SectionRowProps) {
    const badge = behaviorType ? BEHAVIOR_BADGES[behaviorType] : null;
    const isLocked = status === "locked";
    const isNext = status === "next";

    return (
        <Pressable
            onPress={onPress}
            style={({ pressed }) => [
                styles.row,
                !isLast && styles.rowBorder,
                isNext && styles.rowNext,
                isLocked && styles.rowLocked,
                pressed && !isLocked && styles.rowPressed
            ]}
        >
            <StatusDot status={status} />
            <View style={styles.content}>
                <Text style={[styles.name, isLocked && styles.nameLocked]}>{name}</Text>
                <View style={styles.meta}>
                    {behaviorType !== "teaching" ? (
                        <Text style={[styles.questionCount, isLocked && styles.metaLocked]}>
                            {status === "completed" && completedCount != null
                                ? `${completedCount}/${questionCount}`
                                : `${questionCount} questions`}
                        </Text>
                    ) : (
                        <Text style={[styles.questionCount, isLocked && styles.metaLocked]}>
                            Review &amp; complete
                        </Text>
                    )}
                    {badge ? (
                        <View style={[styles.typeBadge, { backgroundColor: badge.bg }]}>
                            <Text style={[styles.typeBadgeText, { color: badge.text }]}>
                                {badge.label}
                            </Text>
                        </View>
                    ) : null}
                </View>
            </View>
            <ChevronOrLock status={status} />
        </Pressable>
    );
}

const styles = StyleSheet.create({
    row: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 14,
        paddingHorizontal: spacing.base
    },
    rowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    rowNext: {
        borderLeftWidth: 4,
        borderLeftColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    rowLocked: {
        opacity: 0.5
    },
    rowPressed: {
        backgroundColor: colors.gray100
    },
    dot: {
        width: 14,
        height: 14,
        borderRadius: 7,
        marginRight: spacing.md,
        alignItems: "center",
        justifyContent: "center"
    },
    dotEmpty: {
        borderWidth: 2,
        borderColor: colors.gray300,
        backgroundColor: "transparent"
    },
    dotActive: {
        backgroundColor: colors.teal400
    },
    dotCompleted: {
        backgroundColor: colors.success500
    },
    dotLocked: {
        backgroundColor: colors.gray300
    },
    checkmark: {
        color: "#FFFFFF",
        fontSize: 9,
        fontWeight: "700"
    },
    lockIcon: {
        color: "#FFFFFF",
        fontSize: 8,
        fontWeight: "700"
    },
    content: {
        flex: 1
    },
    name: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.textPrimary
    },
    nameLocked: {
        color: colors.textTertiary
    },
    meta: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        marginTop: 2
    },
    questionCount: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary
    },
    metaLocked: {
        color: colors.textTertiary
    },
    typeBadge: {
        paddingHorizontal: spacing.sm,
        paddingVertical: 1,
        borderRadius: radius.full
    },
    typeBadgeText: {
        fontFamily: fonts.bold,
        fontSize: 9,
        textTransform: "uppercase",
        letterSpacing: 0.5
    },
    chevron: {
        fontSize: 20,
        color: colors.textTertiary,
        marginLeft: spacing.sm
    },
    lockBadge: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: colors.gray300,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: spacing.sm
    },
    lockBadgeText: {
        color: "#FFFFFF",
        fontSize: 9,
        fontWeight: "700"
    },
    checkBadge: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: colors.success500,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: spacing.sm
    },
    checkBadgeText: {
        color: "#FFFFFF",
        fontSize: 10,
        fontWeight: "700"
    }
});
